import { useEffect, useRef, useState, type ClipboardEvent } from "react";
import {
  detectSignature,
  removeSignature,
  saveSignature,
  skipSignature,
  type SignatureCandidate,
} from "../api/client";
import { cleanSignatureHtml, escapeText, MAX_IMAGE_BYTES, readImageFile } from "../lib/signatureHtml";

type Mode = "first-run" | "settings";
type Step = "intro" | "scanning" | "review" | "error";

interface Props {
  mode: Mode;
  /** The saved signature (data: images inlined), or null. */
  saved: string | null;
  canDetect: boolean;
  onClose: (result: "saved" | "skipped" | "removed" | "closed", html?: string | null) => void;
}

export function SignatureSetup({ mode, saved, canDetect, onClose }: Props) {
  const [step, setStep] = useState<Step>(saved ? "review" : "intro");
  const [candidates, setCandidates] = useState<SignatureCandidate[]>([]);
  const [index, setIndex] = useState(0);
  const [scanned, setScanned] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [empty, setEmpty] = useState(!saved);
  const [edited, setEdited] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const htmlRef = useRef<string>(saved ?? "");

  // Tailwind's base styles set `img { height: auto }`, which overrides an image's
  // height ATTRIBUTE here (and only here: emails carry no Tailwind). Pinning the
  // attribute sizes as inline styles keeps the preview true, and mail clients honour
  // inline sizes too.
  const pinImageSizes = () => {
    for (const img of Array.from(editorRef.current?.querySelectorAll("img") ?? [])) {
      const w = Number(img.getAttribute("width"));
      const h = Number(img.getAttribute("height"));
      if (w > 0 && !img.style.width) img.style.width = `${w}px`;
      if (h > 0 && !img.style.height) img.style.height = `${h}px`;
    }
  };

  // The editor is uncontrolled: React must not re-render its contents under the
  // caret. Content is set only when the source changes (a new candidate, a reset).
  const load = (html: string) => {
    htmlRef.current = html;
    if (editorRef.current) {
      editorRef.current.innerHTML = html;
      pinImageSizes();
      htmlRef.current = editorRef.current.innerHTML;
    }
    setEmpty(!html.trim());
    setEdited(false);
  };

  useEffect(() => {
    if (step === "review" && editorRef.current && editorRef.current.innerHTML !== htmlRef.current) {
      editorRef.current.innerHTML = htmlRef.current;
      pinImageSizes();
      htmlRef.current = editorRef.current.innerHTML;
    }
  }, [step]);

  async function find() {
    setStep("scanning");
    setError(null);
    try {
      const found = await detectSignature();
      if (!found) {
        setNote("I can't read your sent mail from this Outlook. Paste your signature below instead.");
        setCandidates([]);
        load("");
        setStep("review");
        return;
      }
      setScanned(found.scanned);
      setCandidates(found.candidates);
      setIndex(0);
      if (found.candidates.length) {
        load(found.candidates[0].html);
        setNote(null);
      } else {
        load("");
        setNote(
          found.scanned
            ? `I couldn't find a signature in your last ${found.scanned} sent emails. Paste yours below.`
            : "You have no sent emails to read yet. Paste your signature below.",
        );
      }
      setStep("review");
    } catch {
      setError("Couldn't read your sent mail just now.");
      setStep("error");
    }
  }

  function showCandidate(i: number) {
    setIndex(i);
    load(candidates[i].html);
  }

  function pasteOwn() {
    setCandidates([]);
    setNote("Paste your signature into the box. Copy it from a new email in Outlook, or from Outlook's signature settings.");
    load("");
    setStep("review");
    setTimeout(() => editorRef.current?.focus(), 0);
  }

  function insertHtml(html: string) {
    editorRef.current?.focus();
    // execCommand is deprecated but is still the only way to insert at the caret
    // with undo support in every Outlook webview.
    if (!document.execCommand("insertHTML", false, html) && editorRef.current) {
      editorRef.current.insertAdjacentHTML("beforeend", html);
    }
    changed();
  }

  async function onPaste(e: ClipboardEvent<HTMLDivElement>) {
    e.preventDefault();
    const data = e.clipboardData;
    const html = data.getData("text/html");
    const files = Array.from(data.files).filter((f) => f.type.startsWith("image/"));
    if (html) {
      const clean = cleanSignatureHtml(html);
      insertHtml(clean.html);
      if (clean.droppedImages && !files.length) {
        setNote(`${clean.droppedImages === 1 ? "An image" : `${clean.droppedImages} images`} didn't come through with the paste. Use Add image to put ${clean.droppedImages === 1 ? "it" : "them"} back.`);
      }
    } else if (!files.length) {
      insertHtml(escapeText(data.getData("text/plain")));
    }
    for (const f of files) await addImageFile(f);
  }

  async function addImageFile(file: File) {
    if (file.size > MAX_IMAGE_BYTES) {
      setNote(`That image is ${Math.round(file.size / 1024)} KB. Images can be up to ${MAX_IMAGE_BYTES / 1024} KB.`);
      return;
    }
    const src = await readImageFile(file);
    insertHtml(`<img src="${src}" alt="" style="max-width:100%">`);
  }

  function changed() {
    const el = editorRef.current;
    if (!el) return;
    pinImageSizes();
    htmlRef.current = el.innerHTML;
    setEmpty(!el.textContent?.trim() && !el.querySelector("img"));
    setEdited(true);
  }

  async function save() {
    setBusy(true);
    setError(null);
    const source = candidates.length ? (edited ? "edited" : "detected") : "manual";
    const res = await saveSignature(htmlRef.current, source).catch(() => ({ ok: false as const, detail: "Couldn't save the signature. Try again." }));
    setBusy(false);
    if (res.ok) onClose("saved", res.html);
    else setError(res.detail);
  }

  async function notNow() {
    if (mode === "first-run") await skipSignature().catch(() => undefined);
    onClose(mode === "first-run" ? "skipped" : "closed");
  }

  async function remove() {
    setBusy(true);
    try {
      await removeSignature();
      onClose("removed", null);
    } catch {
      setError("Couldn't remove the signature. Try again.");
      setBusy(false);
    }
  }

  const current = candidates[index];

  return (
    <div className="sig-sheet" role="dialog" aria-modal="true" aria-labelledby="sig-title">
      <div className="sig-head">
        <h2 id="sig-title">Your signature on HR drafts</h2>
        {mode === "settings" && (
          <button className="sig-close" onClick={() => onClose("closed")} aria-label="Close">
            ×
          </button>
        )}
      </div>

      {step === "intro" && (
        <div className="sig-body">
          <p>
            Replies you draft from the HR mailbox don't pick up your Outlook signature on their own.{" "}
            {canDetect
              ? "I can find it in your sent emails and add it under every HR draft."
              : "Add it here once and I'll put it under every HR draft."}
          </p>
          <p className="sig-muted">You'll see it before anything is saved, and you can edit it.</p>
          <div className="sig-actions">
            {canDetect ? (
              <button className="card-btn primary" onClick={() => void find()}>
                Find my signature
              </button>
            ) : (
              <button className="card-btn primary" onClick={pasteOwn}>
                Add my signature
              </button>
            )}
            {canDetect && (
              <button className="card-btn" onClick={pasteOwn}>
                Paste it myself
              </button>
            )}
            <button className="sig-link" onClick={() => void notNow()}>
              Not now
            </button>
          </div>
        </div>
      )}

      {step === "scanning" && (
        <div className="sig-body" aria-busy="true">
          <p>Reading your recent sent emails…</p>
          <div className="sig-progress" />
        </div>
      )}

      {step === "error" && (
        <div className="sig-body">
          <p className="sig-error">{error}</p>
          <div className="sig-actions">
            <button className="card-btn primary" onClick={() => void find()}>
              Try again
            </button>
            <button className="card-btn" onClick={pasteOwn}>
              Paste it myself
            </button>
          </div>
        </div>
      )}

      {step === "review" && (
        <div className="sig-body">
          {current && (
            <p className="sig-found">
              Found in {current.support} of your last {scanned} sent emails.
              {current.confidence === "low" && " Check it carefully."}
            </p>
          )}
          {current && current.missingImages > 0 && (
            <p className="sig-warn">
              {current.missingImages === 1 ? "One image" : `${current.missingImages} images`} couldn't be copied. Use Add image
              to put {current.missingImages === 1 ? "it" : "them"} back.
            </p>
          )}
          {note && <p className="sig-note">{note}</p>}

          <div
            ref={editorRef}
            className={`sig-editor${empty ? " is-empty" : ""}`}
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-multiline="true"
            aria-label="Signature"
            data-placeholder="Paste or type your signature"
            onInput={changed}
            onPaste={(e) => void onPaste(e)}
          />
          <p className="sig-muted">Click in the box to edit. Delete anything that isn't part of your signature.</p>

          <div className="sig-tools">
            <button className="sig-link" onClick={() => fileRef.current?.click()}>
              Add image
            </button>
            {candidates.length > 1 && (
              <button className="sig-link" onClick={() => showCandidate((index + 1) % candidates.length)}>
                Not it? Show another ({index + 1} of {candidates.length})
              </button>
            )}
            {current && edited && (
              <button className="sig-link" onClick={() => showCandidate(index)}>
                Undo my edits
              </button>
            )}
            {canDetect && !current && (
              <button className="sig-link" onClick={() => void find()}>
                Search my sent mail
              </button>
            )}
            {current && (
              <button className="sig-link" onClick={pasteOwn}>
                Paste my own instead
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/gif"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) void addImageFile(f);
              }}
            />
          </div>

          {error && <p className="sig-error">{error}</p>}

          <div className="sig-actions">
            <button className="card-btn primary" disabled={busy || empty} onClick={() => void save()}>
              {mode === "settings" && saved ? "Save changes" : "Use this signature"}
            </button>
            {mode === "settings" && saved ? (
              <button className="card-btn" disabled={busy} onClick={() => void remove()}>
                Remove signature
              </button>
            ) : (
              <button className="sig-link" onClick={() => void notNow()}>
                {mode === "first-run" ? "Not now" : "Cancel"}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
